import L from "leaflet"
import "leaflet.nontiledlayer"
import "leaflet-timedimension"

import { formatTime, getTimeZoneLabel } from "../../../lib/timezone"
import { describeResponseBody } from "../../../lib/utils"
import { getRainsatProxyApiUrl } from "../../../store/environment"
import { catchMessage } from "../../../store/reducers/errors"

function buildWmsUrl(sub) {
  const base = sub._wmsUrl
  const params = { ...sub.wmsParams }
  const crs = sub._crs || L.CRS.EPSG3857
  const b = sub._map.getBounds()

  const nw = crs.project(b.getNorthWest())
  const se = crs.project(b.getSouthEast())
  params.bbox = [
    Math.min(nw.x, se.x),
    Math.min(nw.y, se.y),
    Math.max(nw.x, se.x),
    Math.max(nw.y, se.y)
  ].join(",")

  const qs = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&")
  return `${base}?${qs}`
}

// claude: een geëvicte cache-laag kan nog volop een WMS-afbeelding aan het laden zijn (opacity 0, nog
// niet geswapt naar _currentImage/_currentCanvas). removeLayer() haalt 'm van de kaart, maar het lopende
// <img>-verzoek loopt gewoon door tot de browser 'm zelf afrondt — dit annuleert dat verzoek alsnog door
// de src te legen, zodat er geen bandbreedte verspild wordt aan een beeld dat toch wordt weggegooid.
const cancelPendingImageLoad = (sub) => {
  const img = sub?._useCanvas ? sub._currentCanvas?._image : sub?._currentImage
  if (img?.src) img.src = sub.emptyImageUrl
}

// claude: een frame dat in _layers staat maar niet op de kaart ligt kan nooit meer laden:
// NonTiledLayer verversdt alleen lagen die op de kaart liggen, en onRemove zet _loaded terug
// op false. leaflet-timedimension maakt juist zulke wezen aan (isReady() roept
// _getLayerForTime aan, dat een laag aanmaakt maar niet toevoegt), en zodra zijn player op
// de buffer staat te wachten roept die geen prepareNextTimes meer aan — dan voegt niemand ze
// ooit toe en wacht hij eeuwig. Dit zet ze terug op de kaart zodat hun load-event alsnog komt.
const resumePendingFrames = (mapRef, groupRef) => {
  let resumed = 0

  groupRef.eachLayer(l => {
    if (!l._timeDimension || !l._layers) return

    Object.keys(l._layers).forEach(t => {
      const sub = l._layers[t]
      if (!sub || mapRef.hasLayer(sub) || sub.isLoaded?.()) return

      mapRef.addLayer(sub)
      resumed += 1
    })
  })

  const timeDimension = groupRef.timeDimension

  // Is er nog een openstaande tijdsprong, vraag die dan opnieuw op via het event dat de
  // lagen zelf gebruiken (_onNewTimeLoading voegt de laag toe aan de kaart)
  if (timeDimension?.isLoading?.()) {
    timeDimension.fire("timeloading", { time: timeDimension.getCurrentTime() })
  }

  // Laat sporen achter: blijft de control ooit tóch hangen, dan is dit het bewijs of de
  // herstelpoging heeft plaatsgevonden en wat hij aantrof
  if (resumed > 0) {
    console.warn(`[TD] ${resumed} frame(s) opnieuw op de kaart gezet na een bounds-wijziging`)
  }

  return resumed
}

// claude: elke TD-laag registreerde voorheen zijn eigen movestart/zoomstart + moveend/zoomend-listener op
// de kaart (3 lagen = 6 losse listeners, elk met hun eigen eachLayer-loop). Dat schaalt lineair met het
// aantal actieve tijdlagen voor exact dezelfde twee kaart-events. Hier registreren we in plaats daarvan
// één gedeeld listener-paar per kaart (idempotent aangemaakt), die over alle geregistreerde groupRefs heen
// itereert — cachegedrag blijft identiek, alleen het aantal event-bindings op de map niet meer.
function getTDGroupRefs(mapRef) {
  if (mapRef._tdGroupRefs) return mapRef._tdGroupRefs

  const groupRefs = new Set()
  mapRef._tdGroupRefs = groupRefs

  mapRef.on("movestart zoomstart", () => {
    groupRefs.forEach(groupRef => {
      // claude: het frame waarop leaflet-timedimension staat te wachten moet de opruiming
      // overleven. Die wacht op het load-event van dát frame voordat hij _loadingTimeIndex
      // vrijgeeft; gooien we het weg, dan komt dat event nooit en blijft de control (en de
      // player, die op 'timeload' zijn buffer vrijgeeft) eindeloos laden.
      const pendingTime = groupRef.timeDimension?.getCurrentTime?.()

      groupRef.eachLayer(l => {
        if (!l._timeDimension || !l._layers) return
        l._timeCacheForward = 0
        l._timeCacheBackward = 0
        Object.keys(l._layers).forEach(t => {
          const sub = l._layers[t]
          if (sub === l._currentLayer) return          // keep the visible frame
          if (pendingTime && String(t) === String(pendingTime)) return // keep the frame in flight
          if (sub && mapRef.hasLayer(sub)) {
            // claude: zonder dit zet het lege vervang-plaatje alsnog een load-event in gang,
            // waarna de laag zichzelf terugzet in _layers en als "geladen" telt — een leeg
            // frame dat de player als bruikbaar beschouwt.
            sub.off("load")
            cancelPendingImageLoad(sub)
            mapRef.removeLayer(sub)
          }
          delete l._layers[t]
        })
      })
    })
  })

  mapRef.on("moveend zoomend", () => {
    groupRefs.forEach(groupRef => {
      const timeDimension = groupRef.timeDimension
      const n = timeDimension?.getAvailableTimes()?.length || 0
      groupRef.eachLayer(l => {
        if (!l._timeDimension) return
        l._timeCacheForward = n
        l._timeCacheBackward = n
      })

      // claude: de bounds wijzigden mogelijk tijdens het laden, waardoor frames van de kaart
      // zijn gehaald. Zet die terug, anders wacht de player op iets wat nooit meer komt.
      resumePendingFrames(mapRef, groupRef)
    })
  })

  return groupRefs
}

export function createTimeDimensionLayer(mapRef, groupRef, layer, config, callback, dispatch, i18n, token) {
  getTDGroupRefs(mapRef).add(groupRef)

  const TimeDimensionControlCustom = L.Control.TimeDimension.extend({
    _getDisplayDateFormat: function (date) {
      const i18nInstance = this._i18n || i18n
      
      let title = i18nInstance.t(layer.name)
      if (layer.subtitle) {
        title += ` ${i18nInstance.t(layer.subtitle)}`
      }
      // claude: weergave volgt de gekozen tijdzone (UTC/lokaal), zie lib/timezone.js (HSGA-204)
      title += ` ${formatTime(date)} ${getTimeZoneLabel(date)}`
      
      return title
    }
  })

  const paneKey = layer.pane ? layer.pane : `${layer.key}Pane`

  const opacity = layer.options?.opacity !== undefined ? layer.options.opacity : 1

  // Normalize layer.layer to always be an array
  const layerNames = Array.isArray(layer.layer) ? layer.layer : [layer.layer]

  // --- 1. Create a dedicated TimeDimension for this overlay ---
  const timeDimension = new L.TimeDimension({
    requestTimeFromCapabilities: false,
  })

  // --- 2. Create a dedicated control for this overlay ---
  const timeDimensionControl = new TimeDimensionControlCustom({
    timeDimension,
    position: "bottomleft",
    autoPlay: layer.autoPlay || false,
    loopButton: true,
    timeSliderDragUpdate: true,
    playerOptions: {
      transitionTime: layer.transitionTime || 1000,
      loop: layer.loop !== undefined ? layer.loop : true,
      startOver: true
    }
  })

  // Store the initial t and layer for later updates
  timeDimensionControl._i18n = i18n
  timeDimensionControl._layer = layer

  // Helper to refresh the displayed date label
  timeDimensionControl.refreshDateDisplay = function () {
    const currentTime = this.options.timeDimension.getCurrentTime()
    if (!currentTime) return
    const dateEl = this.getContainer()?.querySelector(".timecontrol-date")
    if (dateEl) {
      const newText = this._getDisplayDateFormat(new Date(currentTime))
      dateEl.innerHTML = newText
    }
  }

  timeDimensionControl.addTo(mapRef)

  // Store on groupRef for cleanup on remove
  groupRef.timeDimension = timeDimension
  groupRef.timeDimensionControl = timeDimensionControl
  groupRef._pendingTimeouts = groupRef._pendingTimeouts || []

  // claude: laatste vangnet. De player vuurt elke tick "waiting" af zolang zijn buffer te
  // krap is, maar vraagt in die toestand zelf niets meer op — blijft de buffer leeg, dan
  // spint de play-knop eindeloos. Duurt het wachten te lang, dan proberen we de ontbrekende
  // frames alsnog te laden; valt er niets te laden, dan stoppen we de player zodat de UI in
  // een afgeronde staat komt in plaats van te blijven hangen.
  const WAITING_GRACE = 4000
  let waitingSince = null

  const player = timeDimensionControl._player

  if (player) {
    player.on("waiting", () => {
      if (waitingSince === null) {
        waitingSince = Date.now()
        return
      }

      if (Date.now() - waitingSince < WAITING_GRACE) return

      waitingSince = null

      if (resumePendingFrames(mapRef, groupRef) === 0) {
        console.warn("[TD] player wachtte te lang zonder frames om te laden - gestopt")
        player.stop()
      }
    })

    player.on("running play stop", () => {
      waitingSince = null
    })
  }

  // --- 3. For each layer name, create a WMS + TD layer ---
  // Times are driven by the FIRST layer; the rest follow along
  layerNames.forEach((layerName, index) => {
    const wmsLayer = L.nonTiledLayer.wms(layer.url, {
      pane: paneKey,
      opacity,
      version: "1.3.0",
      crs: L.CRS.EPSG3857,
      layers: layerName,
      styles: layer.styles || "",
      format: "image/png",
      transparent: true,
      crossOrigin: true,
      // claude: geen encodeURIComponent hier — Leaflet's L.Util.getParamString encodeert elke WMS-param
      // al zelf bij het opbouwen van de URL. Dubbel encoderen bleef voor een JWT toevallig onschadelijk
      // (alleen A-Za-z0-9-_. erin, blijft ongewijzigd bij encodeURIComponent), maar corrumpeert een token
      // met bv. +, / of = (dat wordt dan %2B/%2F/%3D i.p.v. correct geëncodeerd).
      token,
      ...layer.options
    })

    // claude: de vaste 100ms-timeout was een gok om te wachten tot de pane bestaat. Die pane komt van
    // een <Pane>-component in HKVMap.js die zelf pas verschijnt na een aparte setPanes-state-update +
    // her-render — dus structureel minstens één extra render/commit-cyclus na deze FeatureGroup's eigen
    // add-event. In plaats van te gokken hoe lang dat duurt, pollen we nu echt op het bestaan van de pane
    // (via mapRef.getPane) en voegen de laag toe zodra hij er is — zonder pane gooit Leaflet's
    // Layer.getPane() anders een fout bij het toevoegen. MAX_ATTEMPTS is een noodrem tegen een oneindige
    // stille retry-loop als er iets structureel misgaat.
    const MAX_ATTEMPTS = 200 // ~3s bij 16ms per poging
    let attempt = 0

    const addTdWmsLayer = () => {
      // Bail out if cleanup ran before we got here
      if (!groupRef.timeDimension) return

      if (!mapRef.getPane(paneKey)) {
        attempt += 1
        if (attempt >= MAX_ATTEMPTS) {
          dispatch(catchMessage({ exception: `Pane "${paneKey}" never appeared — giving up on adding TD layer "${layerName}"` }, "createTimeDimensionLayer"))
          return
        }
        groupRef._pendingTimeouts.push(setTimeout(addTdWmsLayer, 16))
        return
      }

      const tdWmsLayer = L.timeDimension.layer.wms(wmsLayer, {
        timeDimension,
        updateTimeDimension: index === 0,         // only first layer drives the times
        updateTimeDimensionMode: "replace",
        requestTimeFromCapabilities: index === 0, // only first layer fetches capabilities
        getCapabilitiesLayerName: layerName,
        cacheForward: 0,
        cacheBackward: 0,
        setDefaultTime: index === 0,
        getCapabilitiesUrl: `${getRainsatProxyApiUrl()}/fews/getcapabilities`
      })

      const origCreateLayerForTime = tdWmsLayer._createLayerForTime
      tdWmsLayer._createLayerForTime = function (time) {
        const subLayer = origCreateLayerForTime.call(this, time)
        const iso = new Date(time).toISOString()

        subLayer.on("error", async (error) => {
          const sub = error?.target || error?.sourceTarget

          // Leaflet only reports that the <img> failed, never why. Refetching the same URL
          // is the only way to see the status and body behind the failure.
          let detail = "WMS image load failed"
          let status = null

          try {
            const url = buildWmsUrl(sub)
            const res = await fetch(url)
            status = res.status

            // An image body is raw PNG bytes: reading it as text produces unreadable
            // characters, so only a textual body is used as the message
            const contentType = res.headers.get("content-type") || ""
            if (!contentType.startsWith("image/")) {
              const text = await res.text()
              detail = describeResponseBody(text) || detail
            }
          } catch (e) {
            detail = `(could not read body: ${e.message})`
          }

          if (groupRef.timeDimensionControl?._player) {
            try { groupRef.timeDimensionControl._player.stop() } catch (e) { }
          }

          // The retry succeeded, so the image is available again and the failure was
          // transient (a passed rate-limit window, for one). Nothing to report.
          if (status >= 200 && status < 300) {
            console.warn(`WMS image load failed but the retry succeeded (${iso})`)
            return
          }

          // The status is what lets the UI tell a rate limit apart from a real error
          dispatch(catchMessage({ exception: detail.toString().slice(0, 300), status: status }, "createLayerForTime"))
        })

        return subLayer
      }

      groupRef.addLayer(tdWmsLayer)
    }

    groupRef._pendingTimeouts.push(setTimeout(addTdWmsLayer, 0))
  })

  // --- 4. Autoplay once times are loaded ---
  timeDimension.on("availabletimeschanged", function () {
    const times = timeDimension.getAvailableTimes()
    if (times.length === 0) return

    // Filter to configured time range (e.g. nowcast: -3h/+6h, forecast: now/+7d, obs: -2w/now)
    if (layer.timeRange) {
      const now = Date.now()
      const rangeStart = now + (layer.timeRange.startOffset ?? -Infinity)
      const rangeEnd = now + (layer.timeRange.endOffset ?? Infinity)
      const filtered = times.filter(t => t >= rangeStart && t <= rangeEnd)

      if (filtered.length > 0 && filtered.length < times.length) {
        timeDimension.setAvailableTimes(filtered, "replace")
        return  // Event fires again with filtered times; proceed on that call
      }
    }

    // Clear any previous pending timeout for this timeDimension
    if (timeDimension._startTimer) clearTimeout(timeDimension._startTimer)

    timeDimension._startTimer = setTimeout(() => {
      // Bail out if cleanup ran before we got here
      if (!groupRef.timeDimensionControl) return

      groupRef.eachLayer(l => {
        if (l._timeDimension) {
          l._timeCacheForward = times.length
          l._timeCacheBackward = times.length
        }
      })

      timeDimension.setCurrentTime(times[0])

      if (layer.autoPlay && timeDimensionControl._player) {
        timeDimensionControl._player.start()
      }

      const dateEl = timeDimensionControl.getContainer()?.querySelector(".timecontrol-date")
      if (dateEl) {
        dateEl.style.cursor = "text"
        dateEl.addEventListener("click", (e) => e.stopPropagation(), true)
      }
    }, 100) // debounce -- wait for all layers to finish updating times
  })

  if (callback) callback(groupRef)
}

export function destroyTimeDimensionLayer(mapRef, groupRef) {
  if (!groupRef) return

  // 0. Unregister from the shared movestart/moveend cache-eviction listener (see getTDGroupRefs)
  mapRef._tdGroupRefs?.delete(groupRef)

  // 1. Cancel pending add-layer timeouts before they can re-add tiles
  if (groupRef._pendingTimeouts) {
    groupRef._pendingTimeouts.forEach(clearTimeout)
    groupRef._pendingTimeouts = []
  }

  // 2. Cancel the autoplay/setCurrentTime debounce
  if (groupRef.timeDimension?._startTimer) {
    clearTimeout(groupRef.timeDimension._startTimer)
    groupRef.timeDimension._startTimer = null
  }

  // 3. Remove every layer the group is holding (TD WMS + wrapped non-tiled WMS)
  if (typeof groupRef.eachLayer === "function") {
    groupRef.eachLayer(layer => {
      if (layer._baseLayer && mapRef.hasLayer(layer._baseLayer)) {
        mapRef.removeLayer(layer._baseLayer)
      }
      if (mapRef.hasLayer(layer)) {
        mapRef.removeLayer(layer)
      }
    })
    groupRef.clearLayers()
  }

  // 4. Remove the group itself
  if (mapRef.hasLayer(groupRef)) {
    mapRef.removeLayer(groupRef)
  }

  // 5. Stop the player, then tear down the control
  if (groupRef.timeDimensionControl) {
    if (groupRef.timeDimensionControl._player) {
      try { groupRef.timeDimensionControl._player.stop() } catch (e) { }
    }
    groupRef.timeDimensionControl.remove()
    groupRef.timeDimensionControl = null
  }

  // 6. Strip listeners + drop the timeDimension
  if (groupRef.timeDimension) {
    groupRef.timeDimension.off()
    groupRef.timeDimension = null
  }
}

// claude: na het wisselen van de weergave-tijdzone de datumlabels van alle TimeDimension-controls
// op deze kaart opnieuw opbouwen (anders veranderen ze pas bij de volgende tijdstap)
export function refreshTimeDimensionLabels(mapRef) {
  if (!mapRef?._tdGroupRefs) return
  mapRef._tdGroupRefs.forEach(groupRef => {
    groupRef.timeDimensionControl?.refreshDateDisplay?.()
  })
}
